/**
 * cmTableFieldValues — the set of values a CM-table field actually holds.
 *
 * The style editor needs this to fill in every category for a chosen field, and
 * there are two independent sources, each incomplete on its own:
 *
 *  - **`field_schema[field].choices`** — the *declared* domain. Authoritative for
 *    an enumerated column, but plenty of tables declare nothing, and a choice can
 *    be declared and never used.
 *  - **the rows themselves** — the *actual* domain. Catches values that were typed
 *    in before anyone declared choices, and the typos that are exactly what an
 *    operator wants to see, but only covers the rows that were fetched.
 *
 * So both are read, unioned, and the caller is told which source each value came
 * from — a value present in the data but not the schema is usually the interesting
 * one.
 */

/** A `field_schema` entry, narrowed to the parts this module reads. */
export interface FieldSchemaEntry {
  name: string;
  label?: string;
  type?: string;
  choices?: string[];
  [key: string]: any;
}

/** A row, narrowed to the parts this module reads. */
export interface RowLike {
  row_data?: Record<string, any> | null;
  [key: string]: any;
}

export interface CandidateCategoryValues {
  /** The union, sorted for a stable fill order. */
  values: string[];
  /** Values found on at least one row. */
  fromData: string[];
  /** Values declared in `field_schema[field].choices`. */
  fromSchema: string[];
  /** In the data but not declared — typos and pre-schema values surface here. */
  undeclared: string[];
}

/**
 * Normalises one raw cell into category values.
 *
 * Numbers and booleans are stringified because a GeoStyler `==` filter compares
 * against the serialized property, and a multi-value cell (a multi-select field)
 * contributes each of its entries rather than a stringified array.
 */
function cellValues(cell: unknown): string[] {
  if (cell == null) return [];
  if (Array.isArray(cell)) return cell.flatMap(cellValues);
  if (typeof cell === "object") return [];
  const value = String(cell).trim();
  return value ? [value] : [];
}

/**
 * Normalises one raw attribute value into category values, shared with the
 * spatial-dataset editor so both surfaces coerce values identically — a category
 * rule compares against the serialized property, so `2` and `"2"` must not
 * produce two different rules.
 */
export const toCategoryValues = (cell: unknown): string[] => cellValues(cell);

/** Compares so "2" sorts before "10" and casing doesn't scatter related values. */
const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/**
 * Every distinct value of `fieldName` across `rows`, in first-seen order.
 *
 * Reads `row_data[fieldName]` first, then the row's own top-level key —
 * `normaliseCmTableRow` promotes `row_data` onto the row, so a row can arrive
 * either shape depending on which service call produced it.
 */
export function distinctFieldValues(rows: RowLike[] | undefined, fieldName: string): string[] {
  if (!rows?.length || !fieldName) return [];
  const seen = new Set<string>();
  for (const row of rows) {
    const cell = row?.row_data?.[fieldName] !== undefined ? row.row_data![fieldName] : row?.[fieldName];
    for (const value of cellValues(cell)) seen.add(value);
  }
  return [...seen];
}

/** The declared choices for `fieldName`, deduped and trimmed. */
export function declaredFieldChoices(fieldSchema: FieldSchemaEntry[] | undefined, fieldName: string): string[] {
  const field = (fieldSchema ?? []).find((entry) => entry?.name === fieldName);
  if (!Array.isArray(field?.choices)) return [];
  return [...new Set(field!.choices!.flatMap(cellValues))];
}

/** Union of the declared choices and the values actually present in the rows. */
export function candidateCategoryValues(
  rows: RowLike[] | undefined,
  fieldSchema: FieldSchemaEntry[] | undefined,
  fieldName: string
): CandidateCategoryValues {
  const fromData = distinctFieldValues(rows, fieldName);
  const fromSchema = declaredFieldChoices(fieldSchema, fieldName);
  const declared = new Set(fromSchema);

  return {
    values: [...new Set([...fromSchema, ...fromData])].sort(naturalCompare),
    fromData,
    fromSchema,
    undeclared: fromData.filter((value) => !declared.has(value))
  };
}

/**
 * The fields offered as "style by field" options: every `field_schema` entry, plus
 * the key the style already uses if the schema has no entry for it (a style key
 * set before the schema existed must stay selectable, not silently vanish).
 */
export function styleFieldOptions(fieldSchema: FieldSchemaEntry[] | undefined, currentKey?: string): string[] {
  const names = (fieldSchema ?? []).map((entry) => entry?.name).filter((name): name is string => !!name);
  const key = currentKey?.trim();
  if (key && !names.includes(key)) names.push(key);
  return [...new Set(names)];
}
